import React from 'react';
import DsButton from '../../common/DsButton';
import EventListCard from '../../common/events/EventListCard';
import EventListRow from '../../common/events/EventListRow';
import EventListSkeleton from '../../common/events/EventListSkeleton';
import { getEventOccurrenceKey } from '../../../utils/eventOccurrenceKey';
import ArtistEventGenreBadgeRotator from './ArtistEventGenreBadgeRotator';
import ArtistEventListCalendarView from './ArtistEventListCalendarView';
import { DEFAULT_PAGE_SIZE, isToday } from './artistEventListUtils';

function renderGenreBadgesRow(genreList, keyPrefix = 'genre', isConciergeEvent = false) {
  const hasGenres = Array.isArray(genreList) && genreList.length > 0;
  if (!hasGenres && !isConciergeEvent) return null;
  return (
    <div className="event-list-row__badges" aria-label="イベントジャンル">
      {isConciergeEvent && (
        <span
          key={`${keyPrefix}-concierge`}
          className="genre event-list-row__badge"
          style={{ backgroundColor: 'var(--purple-600)', color: 'var(--color-inverse)' }}
        >
          コンシェルジュ
        </span>
      )}
      {hasGenres &&
        genreList.map((genre, index) => {
          const key =
            genre?.id != null
              ? `${keyPrefix}-${genre.id}`
              : `${keyPrefix}-${genre?.value || index}`;
          return (
            <span
              key={key}
              className="genre event-list-row__badge"
              style={{
                backgroundColor: genre?.color || 'var(--indigo-500)',
                color: genre?.text_color || 'var(--color-inverse)',
              }}
            >
              {genre?.label || genre?.name || genre?.value || 'ジャンル'}
            </span>
          );
        })}
    </div>
  );
}

export default function ArtistEventListResults({
  cardGridRef,
  cardGridStyle,
  currentEvents,
  currentPage,
  flyerLandscapeMap,
  flyerRevealIndex,
  getPageNumbers,
  goDetail,
  goToPage,
  handleFlyerLoad,
  isCompactCardLayout,
  isLoading,
  itemsPerPage,
  markFlyerLoaded,
  navigate,
  resolveGenreObjsAll,
  resolveThumbSrc,
  resolveVenueDisplay,
  totalPages,
  viewMode,
}) {
  const renderListRow = (event, index) => {
    const occurrenceKey = getEventOccurrenceKey(event, index);
    const isTodayEvent = isToday(event.event_date);
    const isCancelledEvent = !!event?.canceled_at;
    const imgSrc = resolveThumbSrc(event);
    const isLandscapeFlyer = !!flyerLandscapeMap[occurrenceKey];
    const genreObjsForRow = resolveGenreObjsAll(event.genre);
    const isConciergeEvent = Boolean(event?.is_concierge_event || event?.concierge_application_id);
    const venueDisplay = resolveVenueDisplay(event);
    const venueForRow =
      venueDisplay.name || venueDisplay.link
        ? { ...venueDisplay, name: venueDisplay.name || '会場未設定' }
        : null;
    const flyerReveal = index <= flyerRevealIndex;
    const flyerDelayMs = Math.min(index * 40, 600);
    const flyerDisplayTimeoutMs = index === flyerRevealIndex + 1 ? 1000 : 0;
    const flyerLoading = index < 12 ? 'eager' : 'lazy';
    const flyerFetchPriority = index < 4 ? 'high' : 'auto';
    const handleFlyerReady = () => markFlyerLoaded(index, occurrenceKey);
    const handleRowFlyerLoad = (img) => {
      handleFlyerLoad(occurrenceKey, img);
      handleFlyerReady();
    };

    return (
      <EventListRow
        key={occurrenceKey}
        event={event}
        imgSrc={imgSrc}
        imgAlt={event.name ? `${event.name} のフライヤー` : 'フライヤー'}
        isLandscape={isLandscapeFlyer}
        onFlyerLoad={handleRowFlyerLoad}
        onFlyerError={handleFlyerReady}
        onFlyerDisplayTimeout={handleFlyerReady}
        flyerReveal={flyerReveal}
        flyerDelayMs={flyerDelayMs}
        flyerDisplayTimeoutMs={flyerDisplayTimeoutMs}
        flyerLoading={flyerLoading}
        flyerFetchPriority={flyerFetchPriority}
        onClick={() => goDetail(event.event_uuid)}
        isToday={isTodayEvent}
        todayLabel="本日"
        isCancelled={isCancelledEvent}
        cancelLabel="中止"
        venueDisplay={venueForRow}
        badges={renderGenreBadgesRow(
          genreObjsForRow,
          `row-genre-${event.event_uuid}`,
          isConciergeEvent,
        )}
        progressItems={[]}
      />
    );
  };

  if (viewMode === 'calendar') {
    return <ArtistEventListCalendarView renderEventRow={renderListRow} />;
  }

  if (isLoading) {
    return (
      <EventListSkeleton
        viewMode={viewMode}
        count={Math.max(1, Number(itemsPerPage) || DEFAULT_PAGE_SIZE)}
        cardGridRef={cardGridRef}
        cardGridStyle={cardGridStyle}
      />
    );
  }

  return (
    <>
      {viewMode === 'card' ? (
        <div ref={cardGridRef} className="event-list" style={cardGridStyle}>
          {currentEvents.map((event, index) => {
            const occurrenceKey = getEventOccurrenceKey(event, index);
            const isTodayEvent = isToday(event.event_date);
            const isCancelledEvent = !!event?.canceled_at;
            const imgSrc = resolveThumbSrc(event);
            const isLandscapeFlyer = !!flyerLandscapeMap[occurrenceKey];
            const allGenreObjs = resolveGenreObjsAll(event.genre);
            const isConciergeEvent = Boolean(event?.is_concierge_event || event?.concierge_application_id);
            const rotatorBadges = [
              ...(isConciergeEvent ? [{
                id: 'concierge',
                label: 'コンシェルジュ',
                color: 'var(--purple-600)',
                text_color: 'var(--color-inverse)',
              }] : []),
              ...allGenreObjs,
            ];
            const venueDisplay = resolveVenueDisplay(event);
            const venueForCard =
              venueDisplay.name || venueDisplay.link
                ? { ...venueDisplay, name: venueDisplay.name || '会場未設定' }
                : null;
            const flyerReveal = index <= flyerRevealIndex;
            const flyerDelayMs = Math.min(index * 40, 600);
            const flyerDisplayTimeoutMs = index === flyerRevealIndex + 1 ? 1000 : 0;
            const flyerLoading = index < 12 ? 'eager' : 'lazy';
            const flyerFetchPriority = index < 4 ? 'high' : 'auto';
            const handleFlyerReady = () => markFlyerLoaded(index, occurrenceKey);
            const handleCardFlyerLoad = (img) => {
              handleFlyerLoad(occurrenceKey, img);
              handleFlyerReady();
            };

            return (
              <EventListCard
                key={occurrenceKey}
                event={event}
                imgSrc={imgSrc}
                imgAlt={event.name ? `${event.name} のフライヤー` : 'フライヤー'}
                isLandscape={isLandscapeFlyer}
                onFlyerLoad={handleCardFlyerLoad}
                onFlyerError={handleFlyerReady}
                onFlyerDisplayTimeout={handleFlyerReady}
                flyerReveal={flyerReveal}
                flyerDelayMs={flyerDelayMs}
                flyerDisplayTimeoutMs={flyerDisplayTimeoutMs}
                flyerLoading={flyerLoading}
                flyerFetchPriority={flyerFetchPriority}
                onClick={() => navigate(`/artist/workspaces/event-operations/events/${event.event_uuid}`)}
                isToday={isTodayEvent}
                todayLabel="本日"
                isCancelled={isCancelledEvent}
                cancelLabel="中止"
                venueDisplay={venueForCard}
                badges={rotatorBadges.length > 0 ? <ArtistEventGenreBadgeRotator all={rotatorBadges} /> : null}
                progressItems={[]}
                isCompact={isCompactCardLayout}
              />
            );
          })}
          {currentEvents.length === 0 && (
            <div className="empty-hint">この条件に合うイベントが見つかりません。</div>
          )}
        </div>
      ) : (
        <div className="event-list-rows">
          {currentEvents.map((event, index) => renderListRow(event, index))}
          {currentEvents.length === 0 && (
            <div className="empty-hint">この条件に合うイベントが見つかりません。</div>
          )}
        </div>
      )}

      <div className="pagination">
        <DsButton variant="secondary" size="sm" className="pagination__action" onClick={() => goToPage(1)} disabled={currentPage === 1}>&laquo;</DsButton>
        <DsButton variant="secondary" size="sm" className="pagination__action" onClick={() => currentPage > 1 && goToPage(currentPage - 1)} disabled={currentPage === 1}>&lsaquo;</DsButton>
        {getPageNumbers().map((pageNum) => (
          <DsButton
            key={pageNum}
            variant="secondary"
            size="sm"
            onClick={() => goToPage(pageNum)}
            className={`pagination__action${currentPage === pageNum ? ' active' : ''}`}
          >
            {pageNum}
          </DsButton>
        ))}
        <DsButton variant="secondary" size="sm" className="pagination__action" onClick={() => currentPage < totalPages && goToPage(currentPage + 1)} disabled={currentPage === totalPages}>&rsaquo;</DsButton>
        <DsButton variant="secondary" size="sm" className="pagination__action" onClick={() => goToPage(totalPages)} disabled={currentPage === totalPages}>&raquo;</DsButton>
      </div>
    </>
  );
}
